import { useEffect, useState, useCallback } from "react";
import { Plus, Filter, Edit3, UserCheck } from "lucide-react";
import {
  createSection,
  getAllSections,
  getAllClasses,
  updateSection,
} from "../api/student/studentApi";
import { getAllStaff } from "../api/staffApi";

import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  Button,
  Input,
  Textarea,
  Select,
  Alert,
  Badge,
  Table,
  Modal,
  EmptyState,
} from "../components/ui";

const SectionList = () => {
  const [sections, setSections] = useState([]);
  const [classes, setClasses] = useState([]);
  const [staffList, setStaffList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterClassId, setFilterClassId] = useState("");

  // Page-level filter: which role's staff show up in the Class Teacher dropdowns
  const [teacherRoleFilter, setTeacherRoleFilter] = useState("");

  // Create form
  const [form, setForm] = useState({
    classId: "",
    sectionName: "",
    sectionCode: "",
    description: "",
    classTeacherId: "",
  });
  const [createLoading, setCreateLoading] = useState(false);
  const [createError, setCreateError] = useState("");

  // Edit modal
  const [editSection, setEditSection] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [editLoading, setEditLoading] = useState(false);
  const [editError, setEditError] = useState("");

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [sectionData, classData, staffData] = await Promise.all([
        getAllSections(filterClassId || null),
        getAllClasses(),
        getAllStaff(),
      ]);
      setSections(sectionData || []);
      setClasses(classData || []);
      setStaffList(staffData || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [filterClassId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Build a unique list of roles directly from staffList
  const availableRoles = Array.from(
    new Map(
      staffList
        .filter((staff) => staff.roleId?._id && staff.roleId?.roleName)
        .map((staff) => [staff.roleId._id, staff.roleId])
    ).values()
  );

  // Filtered staff for dropdowns
  const teacherOptions = teacherRoleFilter
    ? staffList.filter((staff) => staff.roleId?._id === teacherRoleFilter)
    : staffList;

  /* ── CREATE ── */
  const handleCreate = async (e) => {
    e.preventDefault();
    // NOTE: the Class field used to be a native <select required>, which gave
    // free browser-level validation. Swapping it for the custom <Select>
    // (needed for consistent styling) means that validation no longer fires
    // on its own, so it's re-implemented here to keep the exact same
    // "can't submit without a class" behavior.
    if (!form.classId) {
      setCreateError("Class is required");
      return;
    }
    try {
      setCreateLoading(true);
      setCreateError("");
      await createSection(form);
      setForm({
        classId: "",
        sectionName: "",
        sectionCode: "",
        description: "",
        classTeacherId: "",
      });
      await loadData();
    } catch (err) {
      setCreateError(err?.response?.data?.message || "Failed to create section");
    } finally {
      setCreateLoading(false);
    }
  };

  /* ── EDIT ── */
  const openEdit = (section) => {
    setEditSection(section);
    setEditForm({
      sectionName: section.sectionName,
      sectionCode: section.sectionCode || "",
      description: section.description || "",
      active: section.active,
      classTeacherId: section.classTeacherId?._id || "",
    });
    setEditError("");
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    try {
      setEditLoading(true);
      setEditError("");
      await updateSection(editSection._id, editForm);
      setEditSection(null);
      await loadData();
    } catch (err) {
      setEditError(err?.response?.data?.message || "Failed to update section");
    } finally {
      setEditLoading(false);
    }
  };

  // ---- Presentation-only derivations below (no data/logic changes) ----

  const roleOptions = [
    { label: "All Roles", value: "" },
    ...availableRoles.map((role) => ({ label: role.roleName, value: role._id })),
  ];

  const classOptions = [
    { label: "Select Class", value: "" },
    ...classes.map((cls) => ({ label: `Class ${cls.className}`, value: cls._id })),
  ];

  const classFilterOptions = [
    { label: "All Classes", value: "" },
    ...classes.map((cls) => ({ label: `Class ${cls.className}`, value: cls._id })),
  ];

  const teacherSelectOptions = [
    { label: "None", value: "" },
    ...teacherOptions.map((staff) => ({
      label: `${staff.name}${staff.staffCode ? ` (${staff.staffCode})` : ""}`,
      value: staff._id,
    })),
  ];

  const statusOptions = [
    { label: "Active", value: "true" },
    { label: "Inactive", value: "false" },
  ];

  const tableRows = sections.map((sec) => ({ ...sec, id: sec._id }));

  const columns = [
    {
      key: "class",
      header: "Class",
      render: (sec) => (
        <span className="font-medium text-neutral-900 dark:text-neutral-100">Class {sec.classId?.className}</span>
      ),
    },
    {
      key: "sectionName",
      header: "Section",
      render: (sec) => <span className="font-semibold text-neutral-900 dark:text-neutral-100">{sec.sectionName}</span>,
    },
    {
      key: "sectionCode",
      header: "Code",
      render: (sec) => sec.sectionCode || "—",
    },
    {
      key: "classTeacher",
      header: "Class Teacher",
      render: (sec) => sec.classTeacherId?.name || "—",
    },
    {
      key: "status",
      header: "Status",
      render: (sec) => <Badge variant={sec.active ? "success" : "danger"}>{sec.active ? "Active" : "Inactive"}</Badge>,
    },
    {
      key: "actions",
      header: "Actions",
      align: "right",
      nowrap: true,
      render: (sec) => (
        <Button
          type="button"
          size="xs"
          variant="ghost"
          leftIcon={<Edit3 className="h-3.5 w-3.5" />}
          onClick={() => openEdit(sec)}
        >
          Edit
        </Button>
      ),
    },
  ];

  return (
    <div className="mx-auto max-w-7xl space-y-5 p-4 antialiased sm:p-6 lg:p-8">
      {/* Page Title & Global Role Filter */}
      <div className="flex flex-col gap-4 border-b border-neutral-200 dark:border-neutral-800 pb-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold tracking-tight text-neutral-900 dark:text-neutral-100">
            Section Management
          </h1>
          <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-0.5">
            Configure class divisions and assign class teachers.
          </p>
        </div>

        {/* Global Role Filter */}
        <div className="flex items-center gap-2">
          <UserCheck className="h-4 w-4 shrink-0 text-neutral-400 dark:text-neutral-500" aria-hidden="true" />
          <span className="whitespace-nowrap text-sm font-medium text-neutral-600 dark:text-neutral-300">
            Teacher Role:
          </span>
          <div className="w-40 sm:w-48">
            <Select
              options={roleOptions}
              value={teacherRoleFilter}
              onChange={setTeacherRoleFilter}
            />
          </div>
        </div>
      </div>

      {/* Create Section Form Card */}
      <Card>
        <CardHeader className="flex flex-row items-center gap-1.5">
          <Plus className="h-3.5 w-3.5 text-neutral-400 dark:text-neutral-500" aria-hidden="true" />
          <CardTitle className="text-xs font-semibold uppercase tracking-wider text-neutral-700 dark:text-neutral-200">
            Add New Section
          </CardTitle>
        </CardHeader>

        <CardContent className="space-y-4">
          {createError && <Alert variant="danger">{createError}</Alert>}

          <form onSubmit={handleCreate} className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div>
              <label className="mb-1.5 block text-sm font-medium text-neutral-900 dark:text-neutral-100">Class *</label>
              <Select
                options={classOptions}
                value={form.classId}
                onChange={(val) => setForm({ ...form, classId: val })}
              />
            </div>

            <Input
              label="Section Name *"
              type="text"
              value={form.sectionName}
              onChange={(e) => setForm({ ...form, sectionName: e.target.value })}
              placeholder="e.g. A, B, C"
              required
            />

            <Input
              label="Section Code"
              type="text"
              value={form.sectionCode}
              onChange={(e) => setForm({ ...form, sectionCode: e.target.value })}
              placeholder="Optional"
            />

            <div>
              <label className="mb-1.5 block text-sm font-medium text-neutral-900 dark:text-neutral-100">
                Class Teacher
              </label>
              <Select
                options={teacherSelectOptions}
                value={form.classTeacherId}
                onChange={(val) => setForm({ ...form, classTeacherId: val })}
                searchable
              />
              {teacherOptions.length === 0 && (
                <p className="mt-1.5 text-sm text-neutral-500 dark:text-neutral-400">
                  No staff found for selected role.
                </p>
              )}
            </div>

            <div className="md:col-span-2">
              <Textarea
                label="Description"
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder="Optional description"
                rows={3}
              />
            </div>

            <div className="md:col-span-2">
              <Button type="submit" variant="primary" isLoading={createLoading}>
                Create Section
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      {/* Sections Roster Table Card */}
      <Card>
        <CardHeader className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <CardTitle className="text-xs font-semibold uppercase tracking-wider text-neutral-700 dark:text-neutral-200">
            All Sections ({sections.length})
          </CardTitle>

          <div className="flex items-center gap-2">
            <Filter className="h-3.5 w-3.5 shrink-0 text-neutral-400 dark:text-neutral-500" aria-hidden="true" />
            <div className="w-full sm:w-48">
              <Select
                options={classFilterOptions}
                value={filterClassId}
                onChange={setFilterClassId}
              />
            </div>
          </div>
        </CardHeader>

        <CardContent className="p-0">
          <Table
            columns={columns}
            data={tableRows}
            isLoading={loading}
            density="compact"
            emptyState={<EmptyState title="No sections found" description="Try adjusting your filter." />}
            className="border-0 shadow-none rounded-none"
          />
        </CardContent>
      </Card>

      {/* Edit Section Modal */}
      <Modal
        isOpen={!!editSection}
        onClose={() => setEditSection(null)}
        title="Edit Section Details"
        footer={
          <>
            <Button type="button" variant="secondary" onClick={() => setEditSection(null)}>
              Cancel
            </Button>
            <Button type="submit" form="edit-section-form" variant="primary" isLoading={editLoading}>
              Save Changes
            </Button>
          </>
        }
      >
        {editError && (
          <Alert variant="danger" className="mb-4">
            {editError}
          </Alert>
        )}

        <form id="edit-section-form" onSubmit={handleEditSubmit} className="space-y-4">
          <Input
            label="Section Name *"
            type="text"
            value={editForm.sectionName}
            onChange={(e) => setEditForm({ ...editForm, sectionName: e.target.value })}
            required
          />

          <Input
            label="Section Code"
            type="text"
            value={editForm.sectionCode}
            onChange={(e) => setEditForm({ ...editForm, sectionCode: e.target.value })}
          />

          <div>
            <label className="mb-1.5 block text-sm font-medium text-neutral-900 dark:text-neutral-100">
              Class Teacher
            </label>
            <Select
              options={teacherSelectOptions}
              value={editForm.classTeacherId}
              onChange={(val) => setEditForm({ ...editForm, classTeacherId: val })}
              searchable
            />
            {teacherOptions.length === 0 && (
              <p className="mt-1.5 text-sm text-neutral-500 dark:text-neutral-400">
                No staff found for selected role.
              </p>
            )}
          </div>

          <Textarea
            label="Description"
            value={editForm.description}
            onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
            rows={3}
          />

          <div>
            <label className="mb-1.5 block text-sm font-medium text-neutral-900 dark:text-neutral-100">Status</label>
            <Select
              options={statusOptions}
              value={editForm.active ? "true" : "false"}
              onChange={(val) => setEditForm({ ...editForm, active: val === "true" })}
            />
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default SectionList;